import { useEffect, useRef } from "react";

/**
 * One scroll listener and one animation frame for the whole page, however many
 * parallax elements are mounted. A listener per element is the usual way this
 * is written and the usual reason a page with a dozen of them stutters.
 */
const targets = new Set();
let frame = 0;
let listening = false;

function update() {
  frame = 0;
  const viewport = window.innerHeight;

  targets.forEach(({ node, speed, axis }) => {
    if (!node) return;
    const rect = node.getBoundingClientRect();

    // Skip anything off screen: reading the rect is cheap, writing styles and
    // forcing the compositor to re-raster is not.
    if (rect.bottom < -200 || rect.top > viewport + 200) return;

    // -1 above the fold, 0 centred, 1 below — so the effect is symmetrical
    // around the middle of the viewport instead of tied to the page top.
    const progress = (rect.top + rect.height / 2 - viewport / 2) / viewport;
    const offset = (progress * speed).toFixed(2);

    node.style.setProperty(axis === "x" ? "--mw-par-x" : "--mw-par-y", `${offset}px`);
  });
}

function schedule() {
  if (!frame) frame = requestAnimationFrame(update);
}

/**
 * Drifts an element as the page scrolls, at `speed` pixels per viewport of
 * travel. Negative speed moves it against the scroll, which is what reads as
 * depth when paired with a foreground that moves normally.
 */
function useParallax({ speed = 60, axis = "y" } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const entry = { node, speed, axis };
    targets.add(entry);

    if (!listening) {
      window.addEventListener("scroll", schedule, { passive: true });
      window.addEventListener("resize", schedule, { passive: true });
      listening = true;
    }

    schedule();

    return () => {
      targets.delete(entry);
      node.style.removeProperty(axis === "x" ? "--mw-par-x" : "--mw-par-y");

      if (targets.size === 0 && listening) {
        window.removeEventListener("scroll", schedule);
        window.removeEventListener("resize", schedule);
        listening = false;
        if (frame) {
          cancelAnimationFrame(frame);
          frame = 0;
        }
      }
    };
  }, [speed, axis]);

  return ref;
}

export default useParallax;
